import type {
  GetMenuAppRestaurantDataResponse,
  PartialFirestoreLocationDoc,
  Restaurant,
} from "api-types";

import type { RestaurantAnalyticsCtx } from "../monitoring/rum";

type PartialAnalyticsCtx = Omit<RestaurantAnalyticsCtx, "menuId" | "source">;

/**
 * Returns a Restaurant document merged with the location details.
 *
 * - Single location (legacy): merges the location's address/phone/email into the restaurant
 *   (backward-compatible with legacy single-location URLs).
 * - showRestaurantAddress true: returns restaurant as-is (original address must be preserved).
 * - Multiple locations or none: returns the restaurant as-is.
 *   Multi-location addresses are rendered independently by the UI.
 */
export const createRestaurantObjectForLocation = (
  restaurantData: GetMenuAppRestaurantDataResponse
): Restaurant => {
  const locations = restaurantData.locations;
  // When showRestaurantAddress is set, the restaurant's own address is displayed separately
  // so we must not overwrite it with a location's address.
  if (restaurantData.showRestaurantAddress) {
    return restaurantData.restaurant;
  }
  if (locations && locations.length === 1) {
    return mergeRestaurantAndLocation(restaurantData.restaurant, locations[0]);
  }
  return restaurantData.restaurant;
};

export const createAnalyticsCtx = (
  restaurantData: GetMenuAppRestaurantDataResponse
): PartialAnalyticsCtx => {
  const locations = restaurantData.locations;

  const analyticsCtx: PartialAnalyticsCtx = {
    uuid: restaurantData.restaurant.uuid,
    name: restaurantData.restaurant.name,
    analyticsName: getAnalyticsName(
      restaurantData.restaurant,
      locations,
      restaurantData.showRestaurantAddress
    ),
    locationId: locations?.length === 1 ? locations[0].id : undefined,
  };
  return analyticsCtx;
};

const getAnalyticsName = (
  restaurant: Restaurant,
  locations: PartialFirestoreLocationDoc[] | null | undefined,
  showRestaurantAddress?: boolean
): string => {
  const names: string[] = [];
  // When showRestaurantAddress is set, include the restaurant's city as the first "location"
  if (showRestaurantAddress) {
    names.push(restaurant.address.city);
  }
  if (locations && locations.length > 0) {
    names.push(...locations.map((l) => l.name));
  }
  if (names.length === 0) {
    // Legacy fallback: no locations and no flag → use restaurant city
    return `${restaurant.name}, ${restaurant.address.city}`;
  }
  return `${restaurant.name}, ${names.join(", ")}`;
};

// Menu App specific merger of Restaurant and Location (single-location only)
const mergeRestaurantAndLocation = (
  restaurant: Restaurant,
  location: PartialFirestoreLocationDoc
): Restaurant => ({
  ...restaurant,
  name: `${restaurant.name}, ${location.name}`,
  address: location.address,
  e164PhoneNumber: location.e164PhoneNumber || restaurant.e164PhoneNumber,
  formattedPhoneNumber:
    location.formattedPhoneNumber || restaurant.formattedPhoneNumber,
  contactEmail: location.contactEmail || restaurant.contactEmail,
  openingHours: location.openingHours || restaurant.openingHours,
  coverImageKey: location.coverImageKey || restaurant.coverImageKey,
  coverImageUrl: location.coverImageUrl || restaurant.coverImageUrl,
});
